import { Button } from "components/Button/Button";
import { Autocomplete } from "components/Form/components/Autocomplete/Autocomplete";
import { IconCollection } from "components/Icon/IconCollection";
import InfoPanel from "components/Layout/InfoPanel";
import Placeholder from "components/Layout/Placeholder";
import NotificationService from "components/Notification/Notification.service";
import RestErrorNotification from "components/Notification/RestErrorNotification";
import RecordLabel from "components/RecordLabel";
import RecordTable from "components/Table/RecordTable";
import { TableColumn } from "components/Table/tableUtils";
import { FeConfigType } from "config/config";
import { useResourceData } from "containers/Resources/screens/ResourcesSingle/useResourceData";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getRelativeUrl } from "utils/service";
import "./ZoneTagList.scss";
import { zoneGraphql as graphql } from "./zoneGraphql";
import {
    addableZones,
    containerIdsOf,
    deriveZoneRows,
    RecordZoneTag,
    ZoneRow,
} from "./zoneTagRows";

interface ZoneOption {
    id: string;
    name: string;
}

const TAG_FIELDS = `
    id
    record_id
    zone_id
    zone_id_zone { name }
    record_id_record { name record_type }
`;

/**
 * "Permission zones" popover on a record screen (RBAC Model G).
 *
 * Lists the zones the record is in -- tagged directly, or inherited from its
 * project, bundle or object template -- and lets the user tag the record into
 * a zone or remove a direct tag. Inherited zones are changed on the container
 * they come from, so they are shown read-only with a link to it.
 */
export const ZoneTagList = () => {
    const { resourceData, recordId } = useResourceData();
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [rows, setRows] = useState<ZoneRow[]>([]);
    const [allZones, setAllZones] = useState<ZoneOption[]>([]);

    const containerIds = useMemo(
        () => containerIdsOf((resourceData ?? {}) as Parameters<typeof containerIdsOf>[0]),
        [resourceData],
    );

    const fetchData = useCallback(async () => {
        if (!recordId) return;
        setIsLoading(true);
        try {
            const data = await graphql(
                `query ($direct: String${containerIds.length ? ", $inherited: String" : ""}) {
                    direct: record_zone_plain_list(filter: $direct, limit: 100) { ${TAG_FIELDS} }
                    ${containerIds.length ? `inherited: record_zone_plain_list(filter: $inherited, limit: 100) { ${TAG_FIELDS} }` : ""}
                    zones: zone_plain_list(order: "+name", limit: 500) { id name }
                }`,
                {
                    direct: JSON.stringify({ field: "record_id", op: "eq", value: recordId }),
                    inherited: JSON.stringify({
                        field: "record_id",
                        op: "in",
                        value: containerIds,
                    }),
                },
            );
            setRows(
                deriveZoneRows(data?.direct as RecordZoneTag[], data?.inherited as RecordZoneTag[]),
            );
            setAllZones((data?.zones ?? []) as ZoneOption[]);
        } catch (error: any) {
            NotificationService.error(
                <RestErrorNotification
                    titleOverride="Sorry, couldn't load the permission zones"
                    error={error}
                />,
            );
        } finally {
            setIsLoading(false);
        }
    }, [recordId, containerIds]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const runChange = useCallback(
        async (title: string, change: () => Promise<unknown>) => {
            setIsSaving(true);
            try {
                await change();
                await fetchData();
            } catch (error: any) {
                NotificationService.error(
                    <RestErrorNotification titleOverride={title} error={error} />,
                );
            } finally {
                setIsSaving(false);
            }
        },
        [fetchData],
    );

    const addZone = useCallback(
        (zoneId: string) => {
            if (!zoneId || !recordId) return;
            runChange("Sorry, couldn't add the record to the zone", () =>
                graphql(
                    `
                        mutation ($record_id: String!, $zone_id: String!) {
                            create_record_zone(
                                body: { record_id: $record_id, zone_id: $zone_id }
                                allow_upsert: false
                            ) {
                                id
                            }
                        }
                    `,
                    { record_id: recordId, zone_id: zoneId },
                ),
            );
        },
        [recordId, runChange],
    );

    const removeTag = useCallback(
        (tagId: string) =>
            runChange("Sorry, couldn't remove the record from the zone", () =>
                graphql(
                    `
                        mutation ($id: String!) {
                            delete_record_zone(id: $id)
                        }
                    `,
                    { id: tagId },
                ),
            ),
        [runChange],
    );

    const zoneOptions = useMemo(
        () => addableZones(allZones, rows).map((zone) => ({ value: zone.id, label: zone.name })),
        [allZones, rows],
    );

    const columns: TableColumn[] = useMemo(
        () => [
            {
                label: "Zone",
                key: "zoneName",
                shown: true,
                sortable: false,
                dto: (row: ZoneRow) => (
                    <RecordLabel
                        key={row.zoneId}
                        link={getRelativeUrl("zone", row.zoneId)}
                        name={row.zoneName}
                        icon={IconCollection.lockOutline}
                    />
                ),
            },
            {
                label: "Comes from",
                key: "source",
                shown: true,
                sortable: false,
                dto: (row: ZoneRow) => (
                    <div className="zoneTagSource">
                        {row.directTagId && (
                            <span className="zoneTagDirect">
                                <span>This record</span>
                                <Button
                                    variation="clear"
                                    size="small"
                                    label="Remove"
                                    disabled={isSaving}
                                    onClick={() => removeTag(row.directTagId as string)}
                                />
                            </span>
                        )}
                        {row.inheritedFrom.map((source) => (
                            <RecordLabel
                                key={source.id}
                                link={getRelativeUrl(source.recordType as FeConfigType, source.id)}
                                name={source.name}
                            />
                        ))}
                    </div>
                ),
            },
        ],
        [isSaving, removeTag],
    );

    return (
        <InfoPanel>
            <InfoPanel.Title>Permission zones</InfoPanel.Title>
            <div className="zoneTagList" style={{ width: 460 }}>
                <p className="zoneTagHint">
                    Only roles with a permission for one of these zones can reach this record.
                    Permissions without a zone do not reach it.
                </p>
                {isLoading ? (
                    <Placeholder.List />
                ) : rows.length === 0 ? (
                    <p className="zoneTagEmpty">
                        This record is in no zone. Any role whose permissions cover it can reach it.
                    </p>
                ) : (
                    <RecordTable
                        columns={columns}
                        data={rows}
                        totalItemCount={rows.length}
                        recordTypes="all"
                        isLoading={false}
                        tableSettingsKey="zoneTagList"
                        options={{
                            toolbar: "none",
                            toolbarFilters: false,
                            selectableRows: "none",
                            showSearch: false,
                            viewColumns: false,
                            jumpToPage: false,
                            pagination: false,
                            density: "high",
                        }}
                    />
                )}
                <div className="zoneTagAdd">
                    <Autocomplete
                        name="add-zone"
                        value={null}
                        onChange={addZone}
                        options={{ list: zoneOptions }}
                        isDisabled={isLoading || isSaving || zoneOptions.length === 0}
                        additionalProps={{
                            clearable: false,
                            fieldWidth: 280,
                            placeholder: zoneOptions.length ? "Add to zone" : "No other zones",
                        }}
                    />
                </div>
            </div>
        </InfoPanel>
    );
};
